// Transport: play and pause, the scrub bar with a tick at every 8-count, and the time. The scrub position and the time are written from the
// frame loop, not rendered; the ticks and the loop band change only when the count or the loop does.

import { useEffect, useMemo, useRef } from 'preact/hooks';
import { engine } from '../engine/engine.js';
import { formatTime } from './actions.js';
import { useEngineState } from './engine-state.js';
import { onFrame } from './frames.js';

/** Thinned so ticks are never closer than this many pixels apart. */
const MIN_TICK_GAP_PX = 3.5;

export function Transport() {
  const s = useEngineState();
  const rail = useRef<HTMLDivElement>(null);
  const range = useRef<HTMLInputElement>(null);
  const time = useRef<HTMLSpanElement>(null);
  const dragging = useRef(false);
  const duration = s.track?.duration ?? 0;
  const ready = s.status !== 'idle' && s.status !== 'decoding' && s.status !== 'error';

  useEffect(() => {
    let lastText = '';
    return onFrame((f) => {
      const text = formatTime(f.song);
      if (text !== lastText && time.current) {
        time.current.textContent = text;
        lastText = text;
        range.current?.setAttribute('aria-valuetext', `${text} of ${formatTime(engine.getState().track?.duration ?? 0)}`);
      }
      if (range.current && !dragging.current) range.current.value = String(f.song);
    });
  }, []);

  // Where the 8-counts start: re-read when the count changes (a tap, a fix, the tracker coming back).
  const ticks = useMemo(() => engine.phraseStarts(), [s.status, s.events, s.lab, s.track?.id, s.halfBeatUnsure, s.tempoBpm]);
  const width = rail.current?.clientWidth ?? 240;
  const every = Math.max(1, Math.ceil(MIN_TICK_GAP_PX / Math.max(1e-6, (width * (ticks[1] ?? 1) - width * (ticks[0] ?? 0)) / Math.max(duration, 1e-6))));
  const shown = ticks.filter((_, i) => i % every === 0);
  const pct = (t: number) => `${(100 * t) / Math.max(duration, 1e-6)}%`;

  return (
    <div class="transport">
      <button
        type="button"
        class="btn play"
        disabled={!ready}
        aria-label={s.playing ? 'Pause' : 'Play'}
        onClick={() => void engine.toggle()}
      >
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
          {s.playing ? <path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor" /> : <path d="M8 5v14l11-7z" fill="currentColor" />}
        </svg>
      </button>
      <div class="scrub" ref={rail}>
        <div class="ticks" aria-hidden="true">
          {s.loop.on && duration > 0 && <span class="loop-band" style={{ left: pct(s.loop.start), width: `${(100 * (s.loop.end - s.loop.start)) / duration}%` }} />}
          {shown.map((t) => <span key={t} class="tick" style={{ left: pct(t) }} />)}
          {s.replay && duration > 0 && <span class="replay-mark" data-testid="replay-mark" style={{ left: pct(s.replay.marker) }} />}
        </div>
        <input
          type="range"
          ref={range}
          min={0}
          max={duration || 1}
          step={0.05}
          disabled={!ready}
          aria-label="Position in the song"
          onPointerDown={() => { dragging.current = true; }}
          onPointerUp={() => { dragging.current = false; }}
          onPointerCancel={() => { dragging.current = false; }}
          onInput={(e) => engine.seek(Number(e.currentTarget.value))}
        />
      </div>
      <span class="time">
        <span ref={time}>0:00</span> / {formatTime(duration)}
      </span>
    </div>
  );
}
